import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { catchError, map } from 'rxjs/operators';
import * as _ from 'lodash';
import { queries } from './queries';

@Injectable({
  providedIn: 'root',
})
export class RewardsService {
  private data: any = {};
  private rewardSubj: BehaviorSubject<any>;
  rewards$: Observable<any>;

  constructor(private apollo: Apollo) {
    this.rewardSubj = new BehaviorSubject<any>(undefined);
    this.rewards$ = this.rewardSubj.asObservable();
  }

  /**
   * This method queries all Reward Types, Categories and items.
   */
  queryAllRewards() {
    this.apollo
      .query({
        query: gql`
          ${queries.allRewardsBenefits}
        `,
      })
      .pipe(catchError((err) => err))
      .subscribe((values: any) => {
        const rows = this.buildRows(values.data);
        this.sortItemsByRewardPriority(rows);

        const data = {
          headers: this.transformHeaders(values.data.allRewardTypes),
          rows: rows,
          rewardsFooter: values.data.allRewardsAndBenefits[0].rewardsFooter,
        };

        this.rewardSubj.next(data);
      });
  }

  /**
   * This method is used to transform response immutable object to POJO.
   * Transforms all Reward Types.
   */
  private transformHeaders(data: any[]) {
    return data.map((header) => {
      header = JSON.parse(JSON.stringify(header));
      header.classes = JSON.parse(header?.classes);
      return header;
    });
  }

  /**
   * This method transforms response immutable object to POJO.
   * Builds row items chunks equal to header count and, flattens each item.
   * Returns an array of rows.
   */
  private buildRows(data: any): any[] {
    data = JSON.parse(JSON.stringify(data));
    const { allRewardsAndBenefits, allRewardTypes } = data;
    let rows = [];

    for (const category of allRewardsAndBenefits[0].categories) {
      if (category.showCategoryLabel) {
        rows.push({
          showCategoryLabel: category.showCategoryLabel,
          label: category._label_,
        });
      }
      let temp = _.chunk(category.items, allRewardTypes.length);
      rows.push(temp);
      temp = [];
    }

    rows = _.flattenDepth(rows, 1);
    this.parseClassesToJSON(rows);

    return rows;
  }

  /**
   * Parses immutable classes object to POJO.
   */
  private parseClassesToJSON(rows: any[]) {
    for (let item of rows) {
      if (Array.isArray(item)) {
        item = Array.from(item, (value: any) => {
          value.classes = JSON.parse(value.classes);
          value.rewardType.classes = JSON.parse(value.rewardType.classes);
          return item;
        });
      }
    }
  }

  /**
   * This method sorts row items by Reward Type's prioirty.
   * Useful to arrange and match each element to it's Reward Type's position.
   */
  private sortItemsByRewardPriority(rows: any[]) {
    for (let item of rows) {
      if (Array.isArray(item)) {
        item = item.sort((a: any, b: any) => {
          return a?.rewardType?.priority - b?.rewardType?.priority;
        });
      }
    }
  }

  /**
   * This method checks if RewardSubject has value.
   * Useful to avoid unnecessary HTTP calls.
   */
  hasValue(): boolean {
    return this.rewardSubj.getValue() !== undefined;
  }
}
